// Вывод формул — KaTeX. Условия приходят с LaTeX внутри \( \), разбор тоже.
// Ввод остаётся простым полем: редактора формул в фазе 1 нет.

import katex from "katex";
import { useMemo } from "react";

type Piece = {
  math: boolean;
  /** Сырой LaTeX у формулы, текст как есть у всего остального. */
  value: string;
  /**
   * Знак препинания, снятый со следующего куска текста и приклеенный к
   * формуле. Формула — неразрывный inline-block, и точка после неё уезжает на
   * следующую строку одна; на подсвеченной формуле это особенно заметно.
   */
  glue: string;
};

const INLINE = /\\\(([\s\S]+?)\\\)/g;

/**
 * Формула со знаком отношения — законченное утверждение, и в разборе темы она
 * подсвечивается. Одиночные «2a», «√5» остаются буквами в предложении: плашка
 * на каждом символе превращает абзац в рябь.
 */
const RELATION = /=|<|>|\\ge|\\le|\\ne|\\approx|\\sim|\\equiv|\\Leftrightarrow/;

/** Знаки, которые нельзя оставлять в начале строки. */
const PUNCT = /^[.,;:!?)»]/;

function split(source: string): Piece[] {
  const pieces: Piece[] = [];
  let last = 0;
  INLINE.lastIndex = 0;
  let match = INLINE.exec(source);
  while (match !== null) {
    if (match.index > last)
      pieces.push({ math: false, value: source.slice(last, match.index), glue: "" });
    pieces.push({ math: true, value: match[1]!, glue: "" });
    last = INLINE.lastIndex;
    match = INLINE.exec(source);
  }
  if (last < source.length) pieces.push({ math: false, value: source.slice(last), glue: "" });

  // Знак препинания сразу за формулой переезжает к ней.
  for (let i = 0; i < pieces.length - 1; i++) {
    const here = pieces[i]!;
    const next = pieces[i + 1]!;
    if (!here.math || next.math || !PUNCT.test(next.value)) continue;
    here.glue = next.value.slice(0, 1);
    next.value = next.value.slice(1);
  }
  return pieces;
}

function render(tex: string, displayMode: boolean): string {
  return katex.renderToString(tex, { throwOnError: false, displayMode });
}

function classOf(tex: string, chip: boolean, display: boolean): string {
  const out = ["math"];
  if (chip) out.push("math--chip");
  if (display) out.push("math--display");
  else if (RELATION.test(tex)) out.push("math--rel");
  return out.join(" ");
}

export function Formula({
  source,
  chip = false,
  display = false,
}: {
  source: string;
  chip?: boolean;
  /**
   * Формула вынесена из строки текста в отдельный блок. Тогда `source` — это
   * сам LaTeX, без обрамляющих `\( \)`, и KaTeX набирает его в выключку:
   * крупнее, с настоящими размерами дробей и знаков.
   */
  display?: boolean;
}) {
  const parts = useMemo(() => {
    const pieces = display ? [{ math: true, value: source, glue: "" }] : split(source);
    return pieces.map((p) =>
      p.math
        ? { ...p, html: render(p.value, display), cls: classOf(p.value, chip, display) }
        : { ...p, html: "", cls: "" },
    );
  }, [source, chip, display]);

  return (
    <>
      {parts.map((piece, i) =>
        piece.math ? (
          <span key={i} className="nobr">
            <span
              className={piece.cls}
              // KaTeX отдаёт готовую разметку; текст в неё приходит из пула задач.
              dangerouslySetInnerHTML={{ __html: piece.html }}
            />
            {piece.glue}
          </span>
        ) : (
          <span key={i}>{piece.value}</span>
        ),
      )}
    </>
  );
}
